import {
	CheckoutOrderFragment,
	OrderAdditionalServiceGdsServiceServiceType,
	SegmentFragment,
	TimelineStep,
	TravellerFieldEnum,
	WidgetTravellersFragment
} from '@websky/graphql';
import { Traveller as WidgetTraveller } from '@websky/every-travel/src/types';
import { addMinutes, parse, setHours, setMinutes } from 'date-fns';
import { API_DATE_FORMAT, capitalizeFirstLetter, format } from '../../../../utils';
import {
	GdsAggregationServiceGroup,
	GdsAggregationServiceInsurance,
	GdsAggregationServiceInsuranceGroups,
	GdsServicesAggregations
} from '../../../types';

export const TO_AIRPORT_INTERNATIONAL_BEFORE_DEPARTURE = 160;
export const TO_AIRPORT_DOMESTIC_BEFORE_DEPARTURE = 90;

const getDefaultTimeline = (international?: boolean) => [
	{
		type: TimelineStep.ToAirport,
		relatedTypes: [TimelineStep.ToAirport],
		minutes: {
			from: international ? TO_AIRPORT_INTERNATIONAL_BEFORE_DEPARTURE : TO_AIRPORT_DOMESTIC_BEFORE_DEPARTURE,
			to: international ? TO_AIRPORT_INTERNATIONAL_BEFORE_DEPARTURE : TO_AIRPORT_DOMESTIC_BEFORE_DEPARTURE
		}
	},
	{
		type: TimelineStep.Airport,
		relatedTypes: [TimelineStep.Airport],
		minutes: {
			from: international ? TO_AIRPORT_INTERNATIONAL_BEFORE_DEPARTURE : TO_AIRPORT_DOMESTIC_BEFORE_DEPARTURE,
			to: 40
		}
	},
	{
		type: TimelineStep.Boarding,
		relatedTypes: [TimelineStep.Boarding, TimelineStep.Flight],
		minutes: {
			from: 40,
			to: 20
		}
	},
	{
		type: TimelineStep.Landing,
		relatedTypes: [TimelineStep.Landing, TimelineStep.FromAirport],
		minutes: {
			from: 0,
			to: 0
		}
	},
	{
		type: 'InCity',
		relatedTypes: ['InCity'],
		minutes: {
			from: 0,
			to: 0
		}
	}
];

export const checkInternationalSegment = (segment: SegmentFragment) => {
	return segment.departure?.airport?.country?.id !== segment.arrival?.airport?.country?.id;
};

export const getTimeline = (servicesGroup: GdsServicesAggregations, segmentId: string, international?: boolean) => {
	return getDefaultTimeline(international).map(timeline => ({
		...timeline,
		services: servicesGroup.services.filter(service => timeline.relatedTypes.includes(service.step)),
		segmentId
	}));
};

const isInsuranceAggregationService = (service: GdsAggregationServiceGroup): boolean =>
	service.type === 'insurance' || service.type === 'insuranceGroups';

const hasSelectedServices = (service: GdsAggregationServiceGroup): boolean => service.selectedServices.length > 0;

export const getSuggestedServices = (
	servicesGroup: GdsServicesAggregations,
	suggestedServicesOrder: Record<string, number>
) => {
	return servicesGroup.services
		.filter(service => {
			if (!service.canAddService && !service.selectedServices.length) {
				return false;
			}

			if (isInsuranceAggregationService(service)) {
				return !hasSelectedServices(service);
			}

			// Extra-only: do not show TimelineStep.Other in Recommend
			if (service.step === TimelineStep.Other) {
				return false;
			}

			const isSuggestedType = service.type in suggestedServicesOrder;
			const isRecommendedStep = service.step === TimelineStep.Recommended;

			return (
				!service.step ||
				(service.type === OrderAdditionalServiceGdsServiceServiceType.Misc
					? isRecommendedStep
					: isRecommendedStep || isSuggestedType)
			);
		})
		.sort((a, b) => suggestedServicesOrder[b.type] - suggestedServicesOrder[a.type]);
};

export const getOtherServices = (servicesGroup: GdsServicesAggregations | undefined): GdsAggregationServiceGroup[] => {
	if (!servicesGroup?.services?.length) {
		return [];
	}

	return servicesGroup.services.filter(service => {
		if (isInsuranceAggregationService(service)) {
			return hasSelectedServices(service);
		}

		return service.step === TimelineStep.Other && (service.canAddService || hasSelectedServices(service));
	});
};

export const getRecommendedCardsServices = (
	services: GdsAggregationServiceGroup[],
	options: { isMobile: boolean; suggestServicesTypes: string[] }
): GdsAggregationServiceGroup[] => {
	const { isMobile, suggestServicesTypes } = options;
	const suggestServices: GdsAggregationServiceGroup[] = [];

	services.forEach(service => {
		if (isInsuranceAggregationService(service)) {
			let hasAllowedPassengers: boolean;

			if (service.type === 'insurance') {
				hasAllowedPassengers = service.services.some(service => {
					(service as GdsAggregationServiceInsurance).allowedPassengers?.length;
				});
			} else if (service.type === 'insuranceGroups') {
				hasAllowedPassengers = service.services.some(service =>
					(service as GdsAggregationServiceInsuranceGroups).groups.some(group =>
						group.insurancePrograms.some(insurance => insurance.allowedPassengers?.length)
					)
				);
			}

			if (!hasAllowedPassengers) {
				return;
			}
		} else if (isMobile || !suggestServicesTypes.includes(service.type)) {
			return;
		}

		suggestServices.push(service);
	});

	return suggestServices;
};

export const addTime = (time: string, minutesAmount: number, targetDate: string) => {
	let date = new Date();

	if (targetDate) {
		date = parse(targetDate, API_DATE_FORMAT, new Date());
	}

	const [hours, minutes] = time.split(':');

	date = setHours(date, parseInt(hours));
	date = setMinutes(date, parseInt(minutes));

	return addMinutes(date, minutesAmount);
};

export const getTime = (date: Date): string => {
	return format(date, 'HH:mm');
};

export const getTravellersValues = (
	travellers: WidgetTravellersFragment[],
	customer: CheckoutOrderFragment['customer']['values']
): WidgetTraveller[] => {
	const customerPhone = customer.find(value => value.type === TravellerFieldEnum.Phone)?.value,
		customerEmail = customer.find(value => value.type === TravellerFieldEnum.Email)?.value;

	return travellers.map(passenger => {
		const traveller: WidgetTraveller = {
			id: passenger.id,
			firstname: '',
			lastname: '',
			date_of_birth: '',
			passport_id: '',
			patronymic: '',
			email: customerEmail,
			phone_number: customerPhone
		};

		passenger.values.forEach(value => {
			switch (value.type) {
				case TravellerFieldEnum.LastName:
					traveller.lastname = capitalizeFirstLetter(value.value?.toLowerCase());
					break;
				case TravellerFieldEnum.FirstName:
					traveller.firstname = capitalizeFirstLetter(value.value?.toLowerCase());

					break;
				case TravellerFieldEnum.MiddleName:
					traveller.patronymic = capitalizeFirstLetter(value.value?.toLowerCase());
					break;

				case TravellerFieldEnum.BirthDate:
					traveller.date_of_birth = value.value;

					break;
				case TravellerFieldEnum.DocNumber:
					traveller.passport_id = value.value;
					break;

				case TravellerFieldEnum.Email:
					traveller.email = value.value ? value.value : traveller.email;
					break;

				case TravellerFieldEnum.Phone:
					traveller.phone_number = value.value ? value.value : traveller.phone_number;
			}
		});

		return traveller;
	});
};
